import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { useCurrentSegment } from '@/composables/useCurrentSegment';
import { SafeNavigation } from '@/utils/safeNavigation';

function useViewGroupsCore() {
  const { currentSegment } = useCurrentSegment();
  const route = useRoute();

  const rawGroups = computed(() => {
    if (!currentSegment.value) return null;
    return currentSegment.value.view_groups;
  });

  const rawCurrentGroup = computed(() => {
    if (!rawGroups.value) return null;
    return rawGroups.value.find((group) => group.id === Number(route.params.groupId));
  });

  const rawNextGroup = computed(() => {
    if (!rawCurrentGroup.value || !rawGroups.value) return null;

    const currentGroupIndex = rawGroups.value.findIndex(
      (group) => group.id === Number(route.params.groupId)
    );

    return rawGroups.value[currentGroupIndex + 1] || null;
  });

  const rawPrevGroup = computed(() => {
    if (!rawCurrentGroup.value || !rawGroups.value) return null;

    const currentGroupIndex = rawGroups.value.findIndex(
      (group) => group.id === Number(route.params.groupId)
    );

    return rawGroups.value[currentGroupIndex - 1] || null;
  });

  return {
    rawGroups,
    rawCurrentGroup,
    rawNextGroup,
    rawPrevGroup
  };
}

export function useViewGroups() {
  const core = useViewGroupsCore();

  return {
    groups: core.rawGroups,
    currentGroup: core.rawCurrentGroup,
    nextGroup: core.rawNextGroup,
    prevGroup: core.rawPrevGroup
  };
}

export function useNavigableViewGroups() {
  const core = useViewGroupsCore();

  const navigableGroups = computed(() => {
    if (!core.rawGroups.value) return null;
    return SafeNavigation.getNavigableGroups(core.rawGroups.value);
  });

  const currentNavigableGroupIndex = computed(() => {
    if (!navigableGroups.value || !core.rawCurrentGroup.value) return -1;
    return navigableGroups.value.findIndex((group) => group.id === core.rawCurrentGroup.value?.id);
  });

  const nextNavigableGroup = computed(() => {
    if (!navigableGroups.value || currentNavigableGroupIndex.value === -1) return null;
    return navigableGroups.value[currentNavigableGroupIndex.value + 1] || null;
  });

  const prevNavigableGroup = computed(() => {
    if (!navigableGroups.value || currentNavigableGroupIndex.value === -1) return null;
    return navigableGroups.value[currentNavigableGroupIndex.value - 1] || null;
  });

  const isCurrentGroupNavigable = computed(() => {
    return SafeNavigation.isNavigableGroup(core.rawCurrentGroup.value);
  });

  return {
    navigableGroups,
    currentNavigableGroup: core.rawCurrentGroup,
    nextNavigableGroup,
    prevNavigableGroup,
    currentNavigableGroupIndex,
    isCurrentGroupNavigable
  };
}

export function useCurrentView() {
  const { currentSegment } = useCurrentSegment();
  const route = useRoute();

  const dataContext = useViewGroups();
  const navigationContext = useNavigableViewGroups();

  const views = computed(() => {
    if (!currentSegment.value) return null;
    return currentSegment.value.view_groups.map((group) => group.views).flat();
  });

  const currentView = computed(() => {
    if (!views.value) return null;
    return views.value.find((view) => view.id === Number(route.params.viewId));
  });

  const nextView = computed(() => {
    if (!currentView.value || !views.value) return null;

    const currentViewIndex = views.value.findIndex(
      (view) => view.id === Number(route.params.viewId)
    );

    const currentNextView = views.value[currentViewIndex + 1];

    return currentNextView?.view_group_id === currentView.value?.view_group_id
      ? currentNextView
      : null;
  });

  const prevView = computed(() => {
    if (!currentView.value || !views.value) return null;

    const currentViewIndex = views.value.findIndex(
      (view) => view.id === Number(route.params.viewId)
    );

    return views.value[currentViewIndex - 1];
  });

  return {
    currentView,
    nextView,
    prevView,

    groups: dataContext.groups,
    currentGroup: dataContext.currentGroup,
    nextGroup: dataContext.nextGroup,
    prevGroup: dataContext.prevGroup,

    navigableGroups: navigationContext.navigableGroups,
    currentNavigableGroup: navigationContext.currentNavigableGroup,
    nextNavigableGroup: navigationContext.nextNavigableGroup,
    prevNavigableGroup: navigationContext.prevNavigableGroup,
    currentNavigableGroupIndex: navigationContext.currentNavigableGroupIndex,
    isCurrentGroupNavigable: navigationContext.isCurrentGroupNavigable,
  };
}
